import React, { useEffect, useState, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { POSITIONS, FOOT, AFRICAN_COUNTRIES, PASSPORT_STATUS } from "../lib/constants";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { Upload, Trash2, ShieldCheck, CheckCircle2, XCircle, Camera, Link as LinkIcon, Plane } from "lucide-react";
import { Link } from "react-router-dom";
import EditProfileDialog from "../components/EditProfileDialog";

const STAT_KEYS = ["speed_kmh", "stamina", "strength", "dribbling", "shooting", "passing", "defending"];

const RADAR_TICK = { fill: "#eab308", fontSize: 11, fontFamily: "Oswald" };

export default function PlayerDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({});
  const [videos, setVideos] = useState([]);
  const [invites, setInvites] = useState([]);
  const [relocations, setRelocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const photoRef = useRef();
  const videoRef = useRef();
  const [videoMeta, setVideoMeta] = useState({ title: "New Highlight", category: "match" });

  const load = useCallback(async () => {
    const [p, v, i, r] = await Promise.all([
      api.get("/players/me").then((r) => r.data),
      api.get("/videos/mine").then((r) => r.data),
      api.get("/player/invites").then((r) => r.data).catch(() => []),
      api.get("/relocation/mine").then((r) => r.data).catch(() => []),
    ]);
    setProfile(p || {});
    setVideos(v || []);
    setInvites(i || []);
    setRelocations(r || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      delete payload.videos; delete payload.ratings;
      const { data } = await api.put("/players/me", payload);
      setProfile(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, photo_path: data.path }));
      toast.success("Photo updated");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploadingPhoto(false); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingVideo(true);
    try {
      // Generate thumbnail client-side
      let thumbPath = "";
      try {
        const thumbBlob = await generateThumbnail(file);
        const tfd = new FormData();
        tfd.append("file", new File([thumbBlob], "thumb.jpg", { type: "image/jpeg" }));
        const { data: t } = await api.post("/upload/photo", tfd, { headers: { "Content-Type": "multipart/form-data" } });
        thumbPath = t.path;
      } catch (err) { console.debug("thumbnail gen skipped", err?.message); }

      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(
        `/upload/video?title=${encodeURIComponent(videoMeta.title)}&category=${encodeURIComponent(videoMeta.category)}${thumbPath ? `&thumbnail_path=${encodeURIComponent(thumbPath)}` : ""}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setVideos((vs) => [data, ...vs]);
      toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploadingVideo(false); }
  };

  async function generateThumbnail(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = url;
      video.onloadeddata = () => {
        video.currentTime = Math.min(1, (video.duration || 2) / 3);
      };
      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => { URL.revokeObjectURL(url); b ? resolve(b) : reject(new Error("no blob")); }, "image/jpeg", 0.7);
      };
      video.onerror = () => { URL.revokeObjectURL(url); reject(new Error("video load fail")); };
    });
  }

  const removeVideo = async (id) => {
    try { await api.delete(`/videos/${id}`); setVideos((vs) => vs.filter((v) => v.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const respondInvite = async (id, status) => {
    try {
      await api.patch(`/player/invites/${id}`, { status });
      toast.success(`Invitation ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStep = async (rid, step, done) => {
    try {
      await api.patch(`/relocation/${rid}/step`, { step_key: step.key, done });
      setRelocations((prev) => prev.map((r) => {
        if (r.id !== rid) return r;
        return { ...r, steps: r.steps.map((s) => (s.key === step.key ? { ...s, done } : s)) };
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeness = (() => {
    const fields = ["age", "height_cm", "weight_kg", "position", "dominant_foot", "nationality", "country", "photo_path", "bio"];
    const filled = fields.filter((f) => profile[f] != null && profile[f] !== "").length;
    return Math.round((filled / fields.length) * 100);
  })();

  const radarData = [
    { attr: "Speed", value: Math.min(100, (profile.speed_kmh || 0) * 3) },
    { attr: "Stamina", value: profile.stamina || 0 },
    { attr: "Strength", value: profile.strength || 0 },
    { attr: "Dribble", value: profile.dribbling || 0 },
    { attr: "Shoot", value: profile.shooting || 0 },
    { attr: "Pass", value: profile.passing || 0 },
    { attr: "Defend", value: profile.defending || 0 },
  ];

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">Player dashboard</div>
            <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">
              {user?.name}
              {profile.verified && (
                <span className="ml-3 inline-flex items-center gap-1 text-sm text-[#eab308] border border-[#eab308] px-2 py-1 align-middle">
                  <ShieldCheck size={14} /> Verified
                </span>
              )}
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <EditProfileDialog />
            <Link to={`/players/${profile.user_id || user?.id}`}>
              <Button variant="outline" className="border-white/20 hover:bg-white/10 rounded-sm uppercase tracking-widest" data-testid="view-public-profile-btn">
                <LinkIcon size={14} className="mr-2" />View public profile
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-12 gap-4">
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-widest text-white/60">Profile completeness</div>
            <div className="mt-2 font-display text-4xl font-bold">{completeness}%</div>
            <Progress value={completeness} className="mt-3" />
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-widest text-white/60">Videos uploaded</div>
            <div className="mt-2 font-display text-4xl font-bold text-[#e65f2b]">{videos.length}</div>
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-widest text-white/60">Trial invitations</div>
            <div className="mt-2 font-display text-4xl font-bold text-[#eab308]">{invites.length}</div>
          </div>
        </div>

        <Tabs defaultValue="profile" className="mt-8">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start p-0 h-auto">
            {["profile", "stats", "videos", "invites", "relocation"].map((t) => (
              <TabsTrigger
                key={t} value={t}
                data-testid={`tab-${t}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#e65f2b] data-[state=active]:bg-transparent data-[state=active]:text-[#e65f2b] uppercase tracking-widest text-xs font-bold px-4 py-3"
              >{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
                <div className="aspect-square border border-white/10 bg-black overflow-hidden mb-3">
                  {profile.photo_path ? (
                    <img src={fileUrl(profile.photo_path)} alt="profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30"><Camera size={48} /></div>
                  )}
                </div>
                <input ref={photoRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="photo-file-input" />
                <Button onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black rounded-sm font-bold uppercase tracking-widest" data-testid="upload-photo-btn">
                  {uploadingPhoto ? "Uploading…" : "Upload photo"}
                </Button>
              </div>
              <div className="md:col-span-8 border border-white/10 bg-[#141414] p-6 grid md:grid-cols-2 gap-4">
                <Field label="Age"><Input type="number" value={profile.age || ""} onChange={(e) => set("age", parseInt(e.target.value) || null)} data-testid="input-age" className={inputCls} /></Field>
                <Field label="Height (cm)"><Input type="number" value={profile.height_cm || ""} onChange={(e) => set("height_cm", parseInt(e.target.value) || null)} data-testid="input-height" className={inputCls} /></Field>
                <Field label="Weight (kg)"><Input type="number" value={profile.weight_kg || ""} onChange={(e) => set("weight_kg", parseInt(e.target.value) || null)} data-testid="input-weight" className={inputCls} /></Field>
                <Field label="Position">
                  <Select value={profile.position || ""} onValueChange={(v) => set("position", v)}>
                    <SelectTrigger data-testid="select-position" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Dominant foot">
                  <Select value={profile.dominant_foot || ""} onValueChange={(v) => set("dominant_foot", v)}>
                    <SelectTrigger data-testid="select-foot" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{FOOT.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Nationality">
                  <Select value={profile.nationality || ""} onValueChange={(v) => set("nationality", v)}>
                    <SelectTrigger data-testid="select-nationality" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AFRICAN_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Country (based)">
                  <Select value={profile.country || ""} onValueChange={(v) => set("country", v)}>
                    <SelectTrigger data-testid="select-country" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AFRICAN_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="City"><Input value={profile.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="input-city" className={inputCls} /></Field>
                <Field label="Academy / Club"><Input value={profile.academy || ""} onChange={(e) => set("academy", e.target.value)} data-testid="input-academy" className={inputCls} /></Field>
                <Field label="Passport status">
                  <Select value={profile.passport_status || ""} onValueChange={(v) => set("passport_status", v)}>
                    <SelectTrigger data-testid="select-passport" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{PASSPORT_STATUS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Academics"><Input value={profile.academics || ""} onChange={(e) => set("academics", e.target.value)} data-testid="input-academics" className={inputCls} placeholder="e.g., Senior Secondary Yr 3" /></Field>
                <div className="md:col-span-2">
                  <Field label="Bio"><Textarea value={profile.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="input-bio" className={`${inputCls} min-h-24`} /></Field>
                </div>
                <div className="md:col-span-2">
                  <Button onClick={save} disabled={saving} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold rounded-sm uppercase tracking-widest h-11 px-8" data-testid="save-profile-btn">
                    {saving ? "Saving…" : "Save profile"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="stats" className="mt-6">
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-6 border border-white/10 bg-[#141414] p-6 space-y-4">
                <Field label="Speed (km/h)"><Input type="number" step="0.1" value={profile.speed_kmh || ""} onChange={(e) => set("speed_kmh", parseFloat(e.target.value) || null)} data-testid="input-speed" className={inputCls} /></Field>
                {["stamina", "strength", "dribbling", "shooting", "passing", "defending"].map((k) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs uppercase tracking-widest text-white/70">
                      <span>{k}</span><span className="text-[#eab308]">{profile[k] || 0}</span>
                    </div>
                    <input
                      type="range" min="0" max="100" value={profile[k] || 0}
                      onChange={(e) => set(k, parseInt(e.target.value))}
                      data-testid={`slider-${k}`}
                      className="w-full accent-[#e65f2b]"
                    />
                  </div>
                ))}
                <Button onClick={save} disabled={saving} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold rounded-sm uppercase tracking-widest h-11 px-8" data-testid="save-stats-btn">
                  {saving ? "Saving…" : "Save stats"}
                </Button>
              </div>
              <div className="md:col-span-6 border border-white/10 bg-[#141414] p-6">
                <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Physical radar</div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#333" />
                      <PolarAngleAxis dataKey="attr" tick={RADAR_TICK} />
                      <Radar dataKey="value" stroke="#e65f2b" fill="#e65f2b" fillOpacity={0.4} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="videos" className="mt-6">
            <div className="border border-white/10 bg-[#141414] p-6 mb-4 grid md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/70">Title</Label>
                <Input value={videoMeta.title} onChange={(e) => setVideoMeta({ ...videoMeta, title: e.target.value })} data-testid="video-title-input" className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/70">Category</Label>
                <Select value={videoMeta.category} onValueChange={(v) => setVideoMeta({ ...videoMeta, category: v })}>
                  <SelectTrigger data-testid="video-category-select" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="match">Match Highlight</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="skill">Skill</SelectItem>
                    <SelectItem value="fitness">Fitness Testing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <input ref={videoRef} type="file" accept="video/*" hidden onChange={uploadVideo} data-testid="video-file-input" />
                <Button onClick={() => videoRef.current?.click()} disabled={uploadingVideo} className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold rounded-sm uppercase tracking-widest h-11" data-testid="upload-video-btn">
                  <Upload size={14} className="mr-2" />{uploadingVideo ? "Uploading…" : "Upload"}
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {videos.map((v) => (
                <div key={v.id} className="border border-white/10 bg-[#141414]">
                  <video controls src={fileUrl(v.storage_path)} poster={v.thumbnail_path ? fileUrl(v.thumbnail_path) : undefined} className="w-full aspect-video bg-black" data-testid={`video-player-${v.id}`} />
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-display uppercase font-bold">{v.title}</div>
                      <div className="text-xs text-white/50 uppercase tracking-widest">{v.category}</div>
                    </div>
                    <button onClick={() => removeVideo(v.id)} className="text-white/60 hover:text-red-400" data-testid={`delete-video-${v.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {videos.length === 0 && <div className="text-white/50">No videos yet. Upload your first highlight.</div>}
            </div>
          </TabsContent>

          <TabsContent value="invites" className="mt-6">
            <div className="space-y-3">
              {invites.length === 0 && <div className="text-white/50">No trial invitations yet. Complete your profile to attract scouts.</div>}
              {invites.map((i) => (
                <div key={i.id} className="border border-white/10 bg-[#141414] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`invite-${i.id}`}>
                  <div className="flex-1 min-w-64">
                    <div className="font-display text-xl uppercase font-bold">{i.club_name}</div>
                    <div className="text-sm text-white/60">{i.location} · {i.date} · from {i.scout_name}</div>
                    <div className="text-sm mt-2">{i.message}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {i.status === "pending" ? (
                      <>
                        <Button
                          onClick={() => respondInvite(i.id, "accepted")}
                          className="bg-[#2A9D8F] hover:bg-[#248277] text-black font-bold rounded-sm uppercase tracking-widest text-xs h-9"
                          data-testid={`invite-accept-${i.id}`}
                        >
                          <CheckCircle2 size={14} className="mr-1" />Accept
                        </Button>
                        <Button
                          onClick={() => respondInvite(i.id, "declined")}
                          variant="outline"
                          className="border-white/20 hover:bg-white/10 rounded-sm uppercase tracking-widest text-xs h-9"
                          data-testid={`invite-decline-${i.id}`}
                        >
                          <XCircle size={14} className="mr-1" />Decline
                        </Button>
                      </>
                    ) : (
                      <div
                        className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${
                          i.status === "accepted" ? "text-[#2A9D8F] border border-[#2A9D8F]/40" : "text-white/50 border border-white/20"
                        }`}
                        data-testid={`invite-status-${i.id}`}
                      >
                        {i.status}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="relocation" className="mt-6">
            <div className="space-y-4">
              {relocations.length === 0 && (
                <div className="text-white/50">
                  No active relocation cases. Accept a trial invitation to open one.
                </div>
              )}
              {relocations.map((r) => {
                const total = r.steps.length;
                const done = r.steps.filter((s) => s.done).length;
                const pct = Math.round((done / total) * 100);
                return (
                  <div key={r.id} className="border border-white/10 bg-[#141414] p-6" data-testid={`relocation-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 text-[#eab308]">
                          <Plane size={16} />
                          <span className="text-xs uppercase tracking-widest">Relocation · {r.status}</span>
                        </div>
                        <div className="font-display text-2xl uppercase font-bold mt-2">{r.club_name}</div>
                        <div className="text-sm text-white/60">Destination: {r.destination} · Target: {r.target_date}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl font-bold text-[#e65f2b]">{pct}%</div>
                        <div className="text-xs uppercase tracking-widest text-white/50">{done} / {total} steps</div>
                      </div>
                    </div>
                    <Progress value={pct} className="mt-4" />
                    <div className="mt-6 grid md:grid-cols-2 gap-2">
                      {r.steps.map((s) => (
                        <label
                          key={s.key}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            s.done ? "border-[#2A9D8F]/40 bg-[#2A9D8F]/5" : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`reloc-step-${r.id}-${s.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={s.done}
                            onChange={(e) => toggleStep(r.id, s, e.target.checked)}
                            className="w-4 h-4 accent-[#2A9D8F]"
                          />
                          <div className={`text-sm uppercase tracking-widest font-bold ${s.done ? "text-[#2A9D8F]" : "text-white"}`}>
                            {s.label}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const inputCls = "bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b] focus-visible:ring-1 focus-visible:ring-[#e65f2b]";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/70 mb-2">{label}</div>
      {children}
    </div>
  );
}
